// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * planNormalizer.js - Fuente única para interpretar límites de planes desde el backend
 * No hardcodear límites. -1 = ilimitado, 0 = no disponible, >0 = valor real.
 * Salida solo estructura normalizada: limits.{ users, messages, invitations, storage }, usage.{ users, messages, invitations, storage, devices_active }.
 *
 * @file frontend/src/utils/planNormalizer.js
 * @description Normaliza response de /api/plan/info.php y /api/tenant/usage.php
 */

import { LEGACY_LIMITS, LEGACY_USAGE, LEGACY_TENANT_STORAGE } from './planLegacyKeys';

/** -1 en backend = ilimitado. 0 = no permitido / sin disponibilidad. */
const UNLIMITED_BACKEND = -1;

/**
 * Indica si el límite debe mostrarse como "Ilimitado" (solo -1).
 * Nunca true para 0, null o undefined.
 *
 * @param {number|null|undefined} value
 * @returns {boolean}
 */
export function isUnlimited(value) {
  return value === UNLIMITED_BACKEND;
}

/**
 * Indica si el límite es "no disponible" (0).
 *
 * @param {number|null|undefined} value
 * @returns {boolean}
 */
export function isNotAvailable(value) {
  return value === 0;
}

/**
 * Formatea un límite para la UI.
 * - -1 → "Ilimitado"
 * - 0  → "No disponible"
 * - >0 → número + unidad
 * Nunca muestra "Ilimitado" por defecto.
 *
 * @param {number|null|undefined} value
 * @param {{ unit?: string, t?: (k: string) => string }} options
 * @returns {string}
 */
export function formatLimit(value, options = {}) {
  const { unit = '', t = (k) => k } = options;
  if (value === UNLIMITED_BACKEND) {
    return t('dashboard.unlimited') || 'Ilimitado';
  }
  if (value === 0 || value == null) {
    return t('dashboard.not_available') || 'No disponible';
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return t('dashboard.not_available') || 'No disponible';
  }
  return `${n}${unit ? ` ${unit}` : ''}`;
}

/**
 * Normaliza la estructura de límites/uso desde el backend.
 * Salida unificada: limits { users, messages, invitations, storage }, usage { users, messages, invitations, storage, devices_active }.
 * Acepta claves legacy (max_*, *_today, storage_mb) del backend.
 *
 * @param {object} apiData - data de GET /api/tenant/usage.php o plan/usage de /api/plan/info.php
 * @returns {{ plan_name: string, limits: object, usage: object, percentages: object }|null}
 */
export function normalizePlanLimits(apiData) {
  if (!apiData || typeof apiData !== 'object') return null;

  const planName = (apiData.plan_name ?? apiData.plan ?? 'free').toString().toLowerCase();
  const limitsRaw = apiData.limits ?? {};
  const usageRaw = apiData.usage ?? {};
  const percentages = apiData.percentages ?? {};

  const normalizeLimit = (v) => {
    if (v === UNLIMITED_BACKEND || v === 0) return v;
    if (v != null && Number.isFinite(Number(v))) return Number(v);
    return null;
  };

  const limits = {
    users: normalizeLimit(limitsRaw.users ?? limitsRaw[LEGACY_LIMITS.users]),
    messages: normalizeLimit(limitsRaw.messages ?? limitsRaw[LEGACY_LIMITS.messages]),
    invitations: normalizeLimit(limitsRaw.invitations ?? limitsRaw[LEGACY_LIMITS.invitations]),
    storage: normalizeLimit(limitsRaw.storage ?? limitsRaw[LEGACY_LIMITS.storage]),
    devices_per_user: normalizeLimit(limitsRaw.devices_per_user ?? limitsRaw[LEGACY_LIMITS.devices_per_user]),
  };

  const usage = {
    users: Number(usageRaw.users) || 0,
    messages: Number(usageRaw.messages ?? usageRaw[LEGACY_USAGE.messages]) || 0,
    invitations: Number(usageRaw.invitations ?? usageRaw[LEGACY_USAGE.invitations]) || 0,
    storage: Number(usageRaw.storage ?? usageRaw[LEGACY_USAGE.storage]) || 0,
    devices_active: Number(usageRaw.devices_active) || 0,
  };

  return {
    plan_name: planName,
    limits,
    usage,
    percentages: {
      users: Number(percentages.users) || 0,
      messages: Number(percentages.messages) || 0,
      invitations: Number(percentages.invitations) || 0,
      storage: Number(percentages.storage) || 0,
    },
  };
}

/**
 * Sustituye placeholders en plantillas de Ayuda. Solo estructura normalizada: limits.{ users, messages, invitations, storage }.
 *
 * @param {string} template - Texto con placeholders (ej. "Hasta {{max_users}} usuarios")
 * @param {object} limits - Objeto limits normalizado { users, messages, invitations, storage }
 * @param {(k: string) => string} t - Función de traducción para "Ilimitado" / "No disponible"
 * @returns {string}
 */
export function substitutePlanPlaceholders(template, limits, t = (k) => k) {
  if (!template || typeof template !== 'string') return '';
  const L = limits && typeof limits === 'object' ? limits : {};
  const pick = (a, b) => (a !== undefined && a !== null ? a : b);
  const users = pick(L.users, L.max_users);
  const messagesMonth = pick(L.messages, L.max_messages_month);
  const messagesDay = L.max_messages_day;
  const invitations = pick(L.invitations, L.max_invitations_per_day);
  const storage = pick(L.storage, L.max_storage_mb);
  const devices = pick(L.devices_per_user, L.max_devices_per_user);
  const chats = pick(L.max_chats, L.chats);
  const replacements = {
    '{{max_users}}': formatLimit(users, { unit: '', t }),
    '{{max_messages_day}}': formatLimit(messagesDay, { unit: '', t }),
    '{{max_messages_month}}': formatLimit(messagesMonth, { unit: '', t }),
    '{{max_invitations_per_day}}': formatLimit(invitations, { unit: '', t }),
    '{{max_storage_mb}}': formatLimit(storage, { unit: ' MB', t }),
    '{{max_devices_per_user}}': formatLimit(devices, { unit: '', t }),
    '{{max_chats}}': formatLimit(chats, { unit: '', t }),
  };
  let out = template;
  for (const [placeholder, value] of Object.entries(replacements)) {
    out = out.split(placeholder).join(value);
  }
  return out;
}

/**
 * Devuelve almacenamiento de tenant (solo lectura desde API admin). Estructura normalizada.
 */
export function getTenantStorage(tenant) {
  if (!tenant || typeof tenant !== 'object') return 0;
  return Number(tenant.storage ?? tenant[LEGACY_TENANT_STORAGE]) || 0;
}

export default {
  isUnlimited,
  isNotAvailable,
  formatLimit,
  normalizePlanLimits,
  substitutePlanPlaceholders,
  getTenantStorage,
};
